/*! @at.govt.nz/buttons v5.1.0 */
/***************************************************************************************************************************************************************
 *
 * buttons function
 *
 * Buttons signal action. Use them to move the user through the service.
 *
 **************************************************************************************************************************************************************/

import React, { Fragment } from 'react';
import PropTypes from 'prop-types';


// The following line will be replaced automatically with generic imports for the ES5 pipeline.
// You can safely ignore this bit if you use this module with pancake
//
import '../css/styles.css';


/**
 * All different kind of button options
 *
 * @type {Object}
 */
const options = {
	'primary': '',
	'secondary': 'at-btn--secondary',
	'tertiary': 'at-btn--tertiary',
	'borderless': 'at-btn--borderless',
};

/**
 * Size options for the button text/icon.
 *
 * @type {{small: string, large: string, medium: string}}
 */
const sizeOptions = {
	'large': 'at-btn--large',
	'medium': '',
	'small': 'at-btn--small',
};

const backgroundColorOptions = {
	'white': '',
	'dark': 'at-btn--dark',
	'alt': 'at-btn--alt',
}

/**
 * Small list of keyboard keys code usually used for navigation
 */
const Keyboard = {
  ARROW_DOWN: 40,
  ARROW_LEFT: 37,
  ARROW_RIGHT: 39,
  ARROW_UP: 38,
  TAB: 9,
};
const NavigationKeys = [Keyboard.TAB, Keyboard.ARROW_LEFT, Keyboard.ARROW_RIGHT, Keyboard.ARROW_DOWN, Keyboard.ARROW_UP];

/**
 * Detection of some problematic browser
 */
export const IS_SAMSUNG_BROWSER = navigator.userAgent.match(/SamsungBrowser/i);


/**
 * DEFAULT
 * The primary button
 *
 * @param  {string}   linkComponent    - The component used for the link
 * @param  {string}   link             - If this is a link the location it goes
 * @param  {string}   children         - Anything inside
 * @param  {string}   as               - The kind of button, can be either 'primary', 'secondary', 'tertiary', default: 'primary'
 * @param  {string}   type             - The type attribute, default: 'button', optional
 * @param  {boolean}  block            - The block option, optional
 * @param  {boolean}  noBorderRadius   - The noBorderRadius option, optional
 * @param  {string}   className        - An additional class, optional
 * @param  {string}   size             - The size of the button text/icon, can be one of 'large', 'medium', 'small', default: 'medium'
 * @param  {string}   backgroundColor  - The page background the button will be place on, can be one of 'white', 'dark', 'alt', default: 'white'
 * @param  {object}   attributeOptions - Any other attribute options
 */
class ATbutton extends React.PureComponent {
	constructor(props) {
		super(props);

		this.buttonRef = React.createRef();
		this.state = {
			keyboardNavigation: false
		}

		this.onClick = this.onClick.bind(this);
		this.onKeyDown = this.onKeyDown.bind(this);
		this.updateKeyboardNavigation = this.updateKeyboardNavigation.bind(this);
	}
	componentDidMount() {
    document.addEventListener('click', this.onClick);
    document.addEventListener('keydown', this.onKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.onClick);
    document.removeEventListener('keydown', this.onKeyDown);
	}

	onClick (event) {
    const clickCount = event.detail;
		// A click count of 0 is triggered when pressing ENTER on a button element, we do not want to do anything here in that case.
		// Samsung Browser will always send event with a click count of 0, they should be excluded from this rule
    if (clickCount !== 0 ||  IS_SAMSUNG_BROWSER) {
			const eventTarget = event.target;
			if (eventTarget.tagName === 'INPUT' && eventTarget.type === 'text') {
				this.updateKeyboardNavigation(true);
			} else {
				this.updateKeyboardNavigation(false);
			}
    }
  };

  onKeyDown (event) {
    if (NavigationKeys.indexOf(event.keyCode) !== -1) {
			this.updateKeyboardNavigation(true);
    }
	};

	updateKeyboardNavigation (value) {
		if (value !== this.state.keyboardNavigation) {
			this.setState({ keyboardNavigation: value });
		}
	}

	focus() {
		this.buttonRef.current.focus();
		this.updateKeyboardNavigation(true);
	}

	render() {
		const { linkComponent, link, children, as, type, noBorderRadius, block, className = '', icon, size, backgroundColor, ...attributeOptions } = this.props;
		const { keyboardNavigation } = this.state;

		if( link ) {
			const LinkComponent = linkComponent;

			// If we are using a normal link
			if( LinkComponent === 'a' ) {
				attributeOptions.href = link;
			}
			// If we are using a link component
			else if( typeof LinkComponent === 'function' ) {
				attributeOptions.to = link;
			}

			return (
				<LinkComponent
					className={ `at-btn ${ className } ${ options[ as ] } ${ sizeOptions[ size ] } ${ backgroundColorOptions [ backgroundColor ] }${noBorderRadius ? ' at-btn--no-border-radius' : ''}${ block ? ' at-btn--block' : '' }${keyboardNavigation ? ' at-btn--keyboard-active' : ''}` }
					{ ...attributeOptions }
					ref={this.buttonRef}
				>
				{ children }
				{ icon && <ATbuttonIcon icon={icon}></ATbuttonIcon> }
				</LinkComponent>
			);
		}
		else {
			return (
				<button
					type={ type }
					className={ `at-btn ${ className } ${ options[ as ] } ${ sizeOptions[ size ] } ${ backgroundColorOptions [ backgroundColor ] }${noBorderRadius ? ' at-btn--no-border-radius' : ''}${ block ? ' at-btn--block' : '' }${keyboardNavigation ? ' at-btn--keyboard-active' : ''}` }
					{ ...attributeOptions }
					ref={this.buttonRef}
				>
					{ children }
					{ icon && <ATbuttonIcon icon={icon}></ATbuttonIcon> }
				</button>
			);
		}
	}
};


class ATbuttonIcon extends React.PureComponent {
	constructor(props) {
		super(props);
	}

	render() {
		const icon = this.props.icon;

		if(!icon) return null;

		if(icon.match(/.svg#/g)) {
			return <svg className="at-btn__icon"><use href={icon}></use></svg>
		}

		return <span className={`at-btn__icon ${icon}`}></span>
	}
}


ATbuttonIcon.propTypes = {
	icon: PropTypes.string
}

ATbutton.propTypes = {
	link: PropTypes.string,
	children: PropTypes.node.isRequired,
	as: PropTypes.oneOf([ 'primary', 'secondary', 'tertiary', 'borderless' ]).isRequired,
	type: PropTypes.string,
	block: PropTypes.bool,
	className: PropTypes.string,
	linkComponent: PropTypes.oneOfType([ PropTypes.string, PropTypes.func ]),
	icon: PropTypes.string,
	size: PropTypes.oneOf(['small', 'medium', 'large']).isRequired,
	backgroundColor: PropTypes.oneOf(['white', 'dark', 'alt']).isRequired,
};

ATbutton.defaultProps = {
	type: 'button',
	as: 'primary',
	linkComponent: 'a',
	size: 'medium',
	backgroundColor: 'white',
};

export default ATbutton;
